// File: src/components/friends/ChatPage.jsx

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { IoArrowBackSharp, IoSearchSharp } from "react-icons/io5";
import { ReactComponent as NoChatSVG } from "../../assets/NoChat.svg";
import ProfileActionSheet from "../ProfileActionSheet";
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';
import { LuSend } from "react-icons/lu";
import MinimalFooter from "../MinimalFooter";

function isStandalonePWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

/****************************************************
 * Helper Functions
 ****************************************************/
function getProfilePicUrl(userObj) {
  if (!userObj?.profilePicUrl) {
    return "/default-avatar.png";
  }
  return userObj.profilePicUrl;
}

function getRelativeTime(dateString) {
  if (!dateString) return "";
  const now = new Date();
  const date = new Date(dateString);
  const diffSeconds = Math.floor((now - date) / 1000);

  if (diffSeconds < 60) return "Now";

  const isSameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isSameDay) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isYesterday) {
    return "Yesterday";
  }

  const opts = { month: "short", day: "numeric" };
  if (date.getFullYear() !== now.getFullYear()) {
    opts.year = "numeric";
  }
  return date.toLocaleDateString([], opts);
}

function sortRoomsByLatest(rooms = []) {
  return [...rooms].sort((a, b) => {
    const aTime = a.lastMessage?.createdAt
      ? new Date(a.lastMessage.createdAt).getTime()
      : 0;
    const bTime = b.lastMessage?.createdAt
      ? new Date(b.lastMessage.createdAt).getTime()
      : 0;
    return bTime - aTime;
  });
}

/****************************************************
 * Styles
 ****************************************************/
const backButtonStyle = {
  backgroundColor: "#000",
  color: "#fff",
  border: "none",
  borderRadius: 999,
  width: 40,
  height: 28,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  fontWeight: 700,
};

const styles = {
  page: {
    width: "100%",
    maxWidth: "900px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    fontFamily:
      "Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    },

  // Tribe logo
  tribeLogoRow: {
    display: "inline-block",
    margin: "0 auto",
    padding: "0px 0",
  },

  /* Chat room */
  chatRoomContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    backgroundColor: "#fff",

  },
  headerRow: {
    flexShrink: 0,
    display: "grid",
    gridTemplateColumns: "48px 1fr 40px",
    alignItems: "center",
    padding: "0px 12px",
    height: 56,
    borderBottom: "1px solid #E7E9EE",
    boxSizing: "border-box",
    backgroundColor: "#fff",
  },
  headerName: {
    margin: 0,
    fontSize: "clamp(1rem, 2.2vw, 1.05rem)",
    fontWeight: 900,
    color: "#000",
    textAlign: "center",
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
  },
  headerAvatar: {
    width: 38,
    height: 38,
    minWidth: 38,
    minHeight: 38,
    borderRadius: "50%",
    objectFit: "cover",
    cursor: "pointer",
    background: "#F3F4F6",
    border: "2px solid #E7E9EE",
    justifySelf: "end",
  },

  messagesArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "0 16px 50px 0",
    backgroundColor: "#FAFAFA",
    WebkitOverflowScrolling: "touch",
  },
  emptyMessages: {
    textAlign: "center",
    color: "#999",
    marginTop: 20,
    fontSize: 13,
  },

  myMessageRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    marginBottom: 14,
  },
  myMessageBubble: {
    maxWidth: "72%",
    padding: "12px 14px",
    marginLeft: 8,
    backgroundColor: "#007BFF",
    color: "#fff",
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.45,
    wordWrap: "break-word",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },
  theirMessageRow: {
    display: "flex",
    alignItems: "flex-end",
    marginBottom: 14,
  },
  bubbleAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 8,
  },
  theirMessageBubble: {
    maxWidth: "65%",
    padding: "12px 14px",
    backgroundColor: "#E0E0E0",
    color: "#111",
    borderRadius: 14,
    fontSize: 12,
    lineHeight: 1.45,
    wordWrap: "break-word",
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
  },
  myBubbleAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    objectFit: "cover",
    marginLeft: 8,
  },
  timeStamp: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: -6,
    marginBottom: 14,
  },

  // Sticky input row
  inputRow: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    padding: 8,
    backgroundColor: "#fff",
    position: "fixed",
    bottom: "env(safe-area-inset-bottom)",
    left: 0,
    right: 0,
    height: "auto",          // <-- let content decide
    boxSizing: "border-box",
    zIndex: 20,
    gap: 8,
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    flex: 1,
  },
  input: {
    flex: 1,
    borderRadius: 10,
    border: "1px solid #E7E9EE",
    padding: "10px 40px 10px 12px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
    resize: "none",
    lineHeight: 1.4,
    maxHeight: 120,
    overflowY: "hidden", // JS toggles to 'auto' at cap
    boxSizing: "border-box",
            fontFamily: "'Open Sans', sans-serif",

  },
  sendIconBtn: {
    position: "absolute",
    top: "50%",
    right: 8,
    transform: "translateY(-50%)",
    width: 28,
    height: 28,
    borderRadius: 999,
    border: "none",
    background: "transparent",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    padding: 0,
  },

  /* Chat list */
  chatListContainer: {
    width: "100%",
    maxWidth: "900px",
    margin: "0 auto",
    fontFamily:
      "Open Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    padding: 16,
    boxSizing: "border-box",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    paddingTop: 12,
  },

  searchBarContainerLikeContact: { marginBottom: 16 },
  searchBarLikeContact: { position: "relative", width: "100%" },
  searchIconLikeContact: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 16,
    color: "#999",
  },
  searchInputLikeContact: {
    width: "100%",
    padding: "10px 36px",
    borderRadius: 10,
    border: "1px solid #E7E9EE",
    backgroundColor: "#FAFAFA",
    fontSize: 14,
    boxSizing: "border-box",
  },

  chatListWrapperLikeContact: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  emptyList: {
    textAlign: "center",
    color: "#999",
    marginTop: 32,
    fontSize: 13,
  },

  chatItemCard: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #E7E9EE",
    borderRadius: 12,
    padding: 12,
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  chatItemAvatarCard: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: 12,
    background: "#F3F4F6",
    border: "2px solid #E7E9EE",
  },
  chatItemTextArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  chatItemTopRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 4,
    minWidth: 0,
    overflow: "hidden",
  },
  chatItemName: {
    fontWeight: 900,
    color: "#000",
    fontSize: 15,
    maxWidth: "70%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    letterSpacing: "-0.01em",
  },
  chatItemTime: {
    fontSize: 12,
    color: "#6B7280",
    flexShrink: 0,
    marginLeft: 8,
  },
  chatItemBottomRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  chatItemSnippet: {
    fontSize: 13.5,
    color: "#4A5568",
    maxWidth: "70%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  unreadBadge: {
    backgroundColor: "#007BFF",
    color: "#fff",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    marginLeft: 6,
    minWidth: 22,
    textAlign: "center",
    fontWeight: 800,
    lineHeight: 1.1,
  },
};

// --- Sticky footer shell (same pattern as ProfilePage) ---
const pageShell = {
  display: "flex",
  flexDirection: "column",
  minHeight: "80vh",
  background: "#FFFFFF",
};
const contentFlex = {
  flex: 1, // lets content grow and pushes footer to the bottom
};

/****************************************************
 * Ghost State Styles (unchanged)
 ****************************************************/
const emptyStateWrapperStyle = {
  display: "flex",
  marginBottom: "0%",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  height: "100%",
  textAlign: "center",
  marginTop: "25%",
};
const circleWrapperStyle = {
  borderRadius: "50%",
  marginBottom: "1rem",
};
const emptyStateTitleStyle = {
  fontSize: "1rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
  color: "#0f172a",       // stronger neutral like main headers
  letterSpacing: "-0.01em",
};

const emptyStateSubtitleStyle = {
  fontSize: ".88rem",
  color: "#6b7280",       // subtle gray for secondary text
  maxWidth: "240px",
  lineHeight: "1.45",
  margin: "0 auto",       // center under title
};


const logoSvgStyle = { 
  width: 72, 
  height: "auto" 
};


/****************************************************
 * New Component: MessageBubble
 ****************************************************/
function MessageBubble({ message, isMe, currentUser, otherUser }) {
  const [showTimestamp, setShowTimestamp] = useState(false);
  const timerRef = useRef(null);

  const handlePressStart = () => {
    timerRef.current = setTimeout(() => {
      setShowTimestamp(true);
    }, 500);
  };

  const handlePressEnd = () => {
    clearTimeout(timerRef.current);
    setShowTimestamp(false);
  };

  return (
    <div
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
    >
      {isMe ? (
        <div style={styles.myMessageRow}>
          <div style={styles.myMessageBubble}>{message.content}</div>
          {currentUser && (
            <img
              src={getProfilePicUrl(currentUser)}
              alt="Avatar"
              style={styles.myBubbleAvatar}
            />
          )}
        </div>
      ) : (
        <div style={styles.theirMessageRow}>
          {otherUser && (
            <img
              src={getProfilePicUrl(otherUser)}
              alt="Avatar"
              style={styles.bubbleAvatar}
            />
          )}
          <div style={styles.theirMessageBubble}>{message.content}</div>
        </div>
      )}
      {showTimestamp && (
        <div
          style={
            isMe
              ? { ...styles.timeStamp, textAlign: "right" }
              : { ...styles.timeStamp, textAlign: "left" }
          }
        >
          {getRelativeTime(message.createdAt)}
        </div>
      )}
    </div>
  );
}

/****************************************************
 * MAIN COMPONENT
 ****************************************************/
export default function ChatPage({
  chatRooms = [],
  selectedRoom,
  messages,
  newMsg,
  onNewMsgChange,
  onSendMessage,
  onOpenRoom,
  onBackToChats,
  searchTerm,
  onSearchTermChange,
  getOtherUserLocal,
  currentUserId,
  onUserBlocked,
}) {
  const [showChatList, setShowChatList] = useState(true);
  const [sortedRooms, setSortedRooms] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // References for auto-scroll + input focus
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);           // fixed-bar textarea
  const inlineInputRef = useRef(null);     // inline (legacy) textarea

  // base heights for "1 line"
  const [baseHeight, setBaseHeight] = useState(0);
  const [inlineBaseHeight, setInlineBaseHeight] = useState(0);

  // Profile ActionSheet (for the other user)
  const [showProfileSheet, setShowProfileSheet] = useState(false);
  const [profileSheetUser, setProfileSheetUser] = useState(null);

  // For keyboard movement
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  // Track input bar height for fixed layout
  const inputBarRef = useRef(null);
  const [inputBarHeight, setInputBarHeight] = useState(56);

  useEffect(() => {
    let timeoutId;
    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (window.visualViewport) {
          const offset = window.innerHeight - window.visualViewport.height;
          setKeyboardOffset(offset > 0 ? offset : 0);
        }
      }, 100);
    }
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
      window.visualViewport.addEventListener("scroll", handleResize);
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
        window.visualViewport.removeEventListener("scroll", handleResize);
      }
    };
  }, []);

  // Measure one-line "base" height from computed styles
  function measureBaseHeight(el) {
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight);
    const line = Number.isFinite(lh) ? lh : parseFloat(cs.fontSize) || 14;
    const pad =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const border =
      (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    return Math.ceil(line + pad + border);
  }

  // Autosize: start from 'auto' so the first size is truly one line,
  // then clamp to base..MAX. Only grows when content wraps.
  const autosize = (el, base) => {
    if (!el || !base) return;
    const MAX = 120;

    el.style.height = "auto";
    const need = el.scrollHeight; // content height
    const next = Math.min(Math.max(need, base), MAX);

    el.style.height = next + "px";
    el.style.overflowY = next >= MAX ? "auto" : "hidden";
  };

  // Establish base heights when entering chat view
  useEffect(() => {
    if (!showChatList) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const b = measureBaseHeight(inputRef.current);
          setBaseHeight(b);
          inputRef.current.style.minHeight = b + "px";
          autosize(inputRef.current, b);
        }
        if (inlineInputRef.current) {
          const b2 = measureBaseHeight(inlineInputRef.current);
          setInlineBaseHeight(b2);
          inlineInputRef.current.style.minHeight = b2 + "px";
          autosize(inlineInputRef.current, b2);
        }
      });
    }
  }, [showChatList, selectedRoom]);

  // Observe input bar height changes (for fixed layout)
  useEffect(() => {
    if (!inputBarRef.current) return;
    const node = inputBarRef.current;
    const measure = () => setInputBarHeight(node.getBoundingClientRect().height);
    measure();
    let ro = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    } else {
      const id = setInterval(measure, 200);
      return () => clearInterval(id);
    }
    return () => ro && ro.disconnect();
  }, [showChatList, selectedRoom]);

  function closeProfileSheet() {
    setProfileSheetUser(null);
    setShowProfileSheet(false);
  }

  async function openProfileSheet(userObj) {
  if (!userObj?._id) return;

  try {
    const resp = await axios.get(
      `${process.env.REACT_APP_API_URL}/users/${userObj._id}`
    );

    const fullUser = resp.data;

    let isHost = false;

    if (fullUser?.email) {
      try {
        const hostResp = await axios.get(
          `${process.env.REACT_APP_API_URL}/promoters/is-host`,
          {
            params: {
              email: fullUser.email,
            },
          }
        );

        isHost = hostResp.data?.isHost === true;
      } catch {}
    }

    setProfileSheetUser({
      ...fullUser,
      isHost,
    });

    setShowProfileSheet(true);
  } catch (err) {
    console.error("[ChatPage] Error fetching otherUser =>", err);
  }
}
  useEffect(() => {
    if (!showChatList) {
      document.body.classList.add("no-scroll");
      document.documentElement.classList.add("no-scroll");
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.classList.remove("no-scroll");
      document.documentElement.classList.remove("no-scroll");
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.classList.remove("no-scroll");
      document.documentElement.classList.remove("no-scroll");
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [showChatList]);

  // Fetch current user
  useEffect(() => {
    if (!currentUserId) return;
    (async () => {
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/${currentUserId}`
        );
        setCurrentUser(resp.data);
      } catch (err) {
        console.error("[ChatPage] Error fetching current user =>", err);
      }
    })();
  }, [currentUserId]);

  // Sort rooms
  useEffect(() => {
    setSortedRooms(sortRoomsByLatest(chatRooms));
  }, [chatRooms]);

  // Show/hide the chat list
  useEffect(() => {
    setShowChatList(!selectedRoom);
  }, [selectedRoom]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, keyboardOffset]);

  // Autosize as text changes
  useEffect(() => {
    autosize(inputRef.current, baseHeight);
    autosize(inlineInputRef.current, inlineBaseHeight);
  }, [newMsg, baseHeight, inlineBaseHeight]);

  // Enhanced onSendMessage
  async function handleEnhancedSendMessage() {
    await onSendMessage();
    onNewMsgChange("");
    requestAnimationFrame(() => {
      autosize(inputRef.current, baseHeight);
      autosize(inlineInputRef.current, inlineBaseHeight);
    });
    if (inputRef.current) {
      inputRef.current.blur();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }

  function handleBack() {
    setShowChatList(true);
    onBackToChats?.();
  }

  /** 1) RENDER CHAT LIST **/
  function renderChatList() {
    if (!chatRooms || chatRooms.length === 0) {
      return (
        <div style={emptyStateWrapperStyle}>
          <div style={circleWrapperStyle}>
            <NoChatSVG width={220} />
          </div>
          <div style={emptyStateTitleStyle}>No Chats</div>
          <div style={emptyStateSubtitleStyle}>
            Start a conversation or add more friends to see your chat list.
          </div>
        </div>
      );
    }

    const filteredRooms = !searchTerm
      ? sortedRooms
      : sortedRooms.filter((room) => {
          const otherUser = getOtherUserLocal(room);
          if (!otherUser) return false;
          const name = `${otherUser.firstName || ""} ${
            otherUser.lastName || ""
          }`.toLowerCase();
          return name.includes(searchTerm.toLowerCase());
        });

    if (filteredRooms.length === 0) {
      return (
        <div style={styles.emptyList}>
          <p>No chats found.</p>
        </div>
      );
    }

    return filteredRooms.map((room) => {
      const otherUser = getOtherUserLocal(room);
      if (!otherUser) return null;

      const fullName = `${otherUser.firstName}${
        otherUser.lastName ? " " + otherUser.lastName.charAt(0) + "." : ""
      }`.trim();
      const lastMsg = room.lastMessage;
      let snippet = "No messages yet";
      let snippetTime = "";
      if (lastMsg?.content) {
        snippet =
          lastMsg.content.length > 35
            ? lastMsg.content.slice(0, 35) + "…"
            : lastMsg.content;
        snippetTime = getRelativeTime(lastMsg.createdAt);
      }
      const unreadCount = room.unreadCount || 0;

      return (
        <div
          key={room._id}
          style={styles.chatItemCard}
          onClick={() => {
            onOpenRoom(room);
            setShowChatList(false);
          }}
        >
          <img
            src={getProfilePicUrl(otherUser)}
            alt="Avatar"
            style={styles.chatItemAvatarCard}
          />
          <div style={styles.chatItemTextArea}>
            <div style={styles.chatItemTopRow}>
              <span style={styles.chatItemName}>{fullName}</span>
              {snippetTime && (
                <span style={styles.chatItemTime}>{snippetTime}</span>
              )}
            </div>
            <div style={styles.chatItemBottomRow}>
              <span style={styles.chatItemSnippet}>{snippet}</span>
              {unreadCount > 0 && (
                <span style={styles.unreadBadge}>{unreadCount}</span>
              )}
            </div>
          </div>
        </div>
      );
    });
  }

  /** 2) RENDER CHAT ROOM (legacy non-fixed alternative — kept intact) **/
  function renderChatRoom() {
    if (!selectedRoom) {
      return (
        <div style={styles.emptyList}>
          <p>Select a chat to start messaging.</p>
        </div>
      );
    }

    const otherUser = getOtherUserLocal(selectedRoom) || {};
    const fullName = `${otherUser.firstName}${
      otherUser.lastName ? " " + otherUser.lastName.charAt(0) + "." : ""
    }`.trim();

return (
  <div
    className="ph-no-capture"
    style={styles.chatRoomContainer}
  >
        {/* HEADER */}
        <div style={styles.headerRow}>
          <button style={backButtonStyle} onClick={handleBack}>
            <IoArrowBackSharp />
          </button>
          <h3 style={styles.headerName}>{fullName}</h3>
          <img
            src={getProfilePicUrl(otherUser)}
            alt="Avatar"
            style={styles.headerAvatar}
            onClick={() => openProfileSheet(otherUser)}
          />
        </div>

        {/* MESSAGES AREA */}
        <div
          style={styles.messagesArea}
          className="show-scrollbar"
          ref={messagesAreaRef}
        >
          {messages && messages.length > 0 ? (
            messages.map((m) => {
              const isMe = m.senderId === currentUserId;
              return (
                <MessageBubble
                  key={m._id}
                  message={m}
                  isMe={isMe}
                  currentUser={isMe ? currentUser : null}
                  otherUser={!isMe ? otherUser : null}
                />
              );
            })
          ) : (
            <div style={styles.emptyMessages}>No messages yet. Say hi!</div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* INPUT ROW (inline) */}
        <div
          style={{
            ...styles.inputRow,
            position: "relative",
            bottom: undefined,
            transform: keyboardOffset ? `translateY(-${keyboardOffset}px)` : "none",
            zIndex: 2,
          }}
        >
          <div style={styles.inputWrapper}>
            <textarea
              ref={inlineInputRef}
              style={styles.input}
              placeholder="Type a message..."
              value={newMsg}
              onChange={(e) => onNewMsgChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEnhancedSendMessage();
                }
              }}
              rows={1}
            />
            <button
              type="button"
              aria-label="Send message"
              style={{
                ...styles.sendIconBtn,
                cursor: newMsg.trim() ? "pointer" : "default",
                opacity: newMsg.trim() ? 1 : 0.5,
              }}
              disabled={!newMsg.trim()}
              onClick={handleEnhancedSendMessage}
            >
              <LuSend size={18} color={newMsg.trim() ? "#007BFF" : "#A0AEC0"} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /** 3) Render Tribe Header (top of chat) - MOBILE STYLE — kept for parity **/
  function renderTribeHeader() {
    const tribeHeaderStyles = {
      container: {
        backgroundColor: "#000",
        padding: "0.2rem 0.95rem",
        borderRadius: 4,
        textAlign: "center",
        display: "inline-block",
        margin: "0 auto",
      },
      text: {
        fontSize: "16px",
        fontWeight: "bold",
        color: "#fff",
        textTransform: "lowercase",
        borderRadius: 4,
        marginBottom: "0.15rem",
        fontFamily: "'Open Sans', sans-serif",
      },
    };

    return (
      <div style={tribeHeaderStyles.container}>
        <span style={tribeHeaderStyles.text}>tribe</span>
      </div>
    );
  }

    /** MAIN RENDER **/
    if (showChatList) {
  return (
    <div
      className="ph-no-capture"
      style={pageShell}
    >
          <div style={contentFlex}>
            <div style={styles.chatListContainer}>
              <div style={styles.searchBarContainerLikeContact}>
                <div style={styles.searchBarLikeContact}>
                  <IoSearchSharp style={styles.searchIconLikeContact} />
                  <input
                    type="text"
                    placeholder="Search..."
                    style={styles.searchInputLikeContact}
                    value={searchTerm}
                    onChange={(e) => onSearchTermChange(e.target.value)}
                  />
                </div>
              </div>
  
              <div style={styles.chatListWrapperLikeContact}>
                {renderChatList()}
              </div>
            </div>
          </div>
  
          {/* Footer only on chat list */}
          <MinimalFooter />
        </div>
      );
    }
  
    // Chat room view (unchanged)
  return (
  <div
    className="ph-no-capture"
    style={{
      height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
        }}
      >
        {/* Tribe Logo Row (above the header row) */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            background: "#fff",
            height: 92, // logo + header row
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <div style={styles.tribeLogoRow}>
            <TribeLogo style={{ width: 72, height: "auto" }} />
          </div>
  
          <div style={styles.headerRow}>
            <button style={backButtonStyle} onClick={handleBack}>
              <IoArrowBackSharp />
            </button>
            <h3 style={styles.headerName}>
              {getOtherUserLocal(selectedRoom)?.firstName}
            </h3>
            <img
              src={getProfilePicUrl(getOtherUserLocal(selectedRoom))}
              alt="Avatar"
              style={styles.headerAvatar}
              onClick={() => openProfileSheet(getOtherUserLocal(selectedRoom))}
            />
          </div>
        </div>
  
        {/* Fixed input bar at bottom (auto-height) */}
        <div
          ref={inputBarRef}
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            background: "#fff",
            padding: 8,
          }}
        >
          <div style={styles.inputWrapper}>
            <textarea
              ref={inputRef}
              style={styles.input}
              placeholder="Type a message..."
              value={newMsg}
              onChange={(e) => onNewMsgChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEnhancedSendMessage();
                }
              }}
              rows={1}
            />
            <button
              type="button"
              aria-label="Send message"
              style={{
                ...styles.sendIconBtn,
                cursor: newMsg.trim() ? "pointer" : "default",
                opacity: newMsg.trim() ? 1 : 0.5,
              }}
              disabled={!newMsg.trim()}
              onClick={handleEnhancedSendMessage}
            >
              <LuSend size={18} color={newMsg.trim() ? "#007BFF" : "#A0AEC0"} />
            </button>
          </div>
        </div>
  
        {/* Scrollable messages area — bottom follows live input bar height */}
        <div
          ref={messagesAreaRef}
          style={{
            position: "fixed",
            top: 92, // header height
            left: 0,
            right: 0,
            bottom: inputBarHeight, // dynamic to avoid overlap
            overflowY: "auto",
            backgroundColor: "#FDFDFD",
            WebkitOverflowScrolling: "touch",
            padding: "20px 16px",
            zIndex: 10,
          }}
          className="show-scrollbar"
        >
          {messages && messages.length > 0 ? (
            messages.map((m) => {
              const isMe = m.senderId === currentUserId;
              return (
                <MessageBubble
                  key={m._id}
                  message={m}
                  isMe={isMe}
                  currentUser={isMe ? currentUser : null}
                  otherUser={!isMe ? getOtherUserLocal(selectedRoom) : null}
                />
              );
            })
          ) : (
            <div style={styles.emptyMessages}>No messages yet. Say hi!</div>
          )}
          <div ref={messagesEndRef} />
        </div>
  
        {/* Legacy non-fixed input row kept (not visible behind fixed bar) */}
        <div
          style={{
            ...styles.inputRow,
            position: "relative",
            bottom: undefined,
            zIndex: 2,
          }}
        >
          <div style={styles.inputWrapper}>
            <textarea
              ref={inlineInputRef}
              style={styles.input}
              placeholder="Type a message..."
              value={newMsg}
              onChange={(e) => onNewMsgChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEnhancedSendMessage();
                }
              }}
              rows={1}
            />
            <button
              type="button"
              aria-label="Send message"
              style={{
                ...styles.sendIconBtn,
                cursor: newMsg.trim() ? "pointer" : "default",
                opacity: newMsg.trim() ? 1 : 0.5,
              }}
              disabled={!newMsg.trim()}
              onClick={handleEnhancedSendMessage}
            >
              <LuSend size={18} color={newMsg.trim() ? "#007BFF" : "#A0AEC0"} />
            </button>
          </div>
        </div>
  
        {/* Profile ACTION SHEET */}
        {showProfileSheet && profileSheetUser && (
          <ProfileActionSheet
  open={showProfileSheet}
  onClose={closeProfileSheet}
  user={profileSheetUser}
  isHost={profileSheetUser?.isHost === true}
  onUserBlocked={onUserBlocked}
/>
        )}
      </div>
    );
  
}
